import { Box, InputLabel, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import BaseRow from '../base-row/BaseRow';
import { useTranslation } from 'react-i18next';
import BaseSelect from '../base-select/BaseSelect';
import BaseRichEditor from '../base-rich-editor/BaseRichEditor';

import styles from './BaseSendEmail.module.scss';
import classNames from 'classnames/bind';
import { useAppSelector } from '~/redux/hooks';
import { UploadFile } from '~/utils/types/common';

import FileItem from '~/components/estimate/file-item/FileItem';
import { FILE_ITEM_FOR_CUSTOMER, FILE_ITEM_FOR_TASK } from '~/utils/constants/jobs';

const cx = classNames.bind(styles);

export type BaseSendEmailProps = {
  value?: {
    from?: string;
    to?: string;
    emailInfoCC?: string;
    emailInfoBCC?: string;
    subject?: string;
    template?: string;
    files?: UploadFile[] | undefined;
    note?: string;
    jobFileIds?: Array<string>;
    customerFileIds?: Array<string>;
  };
  jobId?: string;
  customerId?: string;
  onChange: (updatedValues: any) => void;
  formik: any;
  fileType: string;
};

const BaseSendEmail = (props: BaseSendEmailProps) => {
  //#region Destructuring Props
  const { value, onChange } = props;
  const { t } = useTranslation(['admin/component', 'admin/common']);
  //#endregion Destructuring Props

  const { templateList } = useAppSelector((state) => state.job);
  const { profile } = useAppSelector((state) => state.auth);

  //#region Declare State

  const [formValues, setFormValues] = useState({
    from: value?.from || profile.email,
    to: value?.to || '',
    cc: value?.emailInfoCC || '',
    bcc: value?.emailInfoBCC || '',
    subject: value?.subject || '',
    template: value?.template || '',
    files: value?.files || undefined,
    note: value?.note || '',
    jobFileIds: value?.jobFileIds || [],
    customerFileIds: value?.customerFileIds || [],
  });

  //#endregion Declare State

  //#region Implement Hook

  //#endregion Implement Hook
  //#region Handle Function
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedValues = { ...formValues, [name]: value };
    setFormValues(updatedValues);
    onChange(updatedValues);
  };

  const handleSelectChange = (value: string) => {
    const selectedTemplate = templateList.find((template) => template.id === value);
    const updatedValues = {
      ...formValues,
      template: value,
      note: selectedTemplate?.content || '',
    };

    setFormValues(updatedValues);
    onChange(updatedValues);
  };

  //#endregion Handle Function
  return (
    <Box className={cx('base-send-email-container')}>
      <Typography>{t('email.emailInformation')}</Typography>
      <BaseRow className='gap-[20px] mb-[20px] mt-[20px]'>
        <Box className='w-full'>
          <InputLabel>{t('email.from')}</InputLabel>
          <TextField
            name='from'
            placeholder={t('email.from')}
            value={formValues.from}
            onChange={handleInputChange}
            error={Boolean(props.formik.errors?.from)}
            helperText={props.formik.errors?.from}
          />
        </Box>
      </BaseRow>
      <BaseRow className='gap-[20px] mb-[20px] mt-[20px]'>
        <Box className='w-full'>
          <InputLabel>{t('email.to')}</InputLabel>
          <TextField
            name='to'
            placeholder={t('email.to')}
            value={formValues.to}
            onChange={handleInputChange}
            error={Boolean(props.formik.errors?.to)}
            helperText={props.formik.errors?.to}
          />
        </Box>

        <Box className='w-full'>
          <InputLabel>{t('email.cc')}</InputLabel>
          <TextField
            name='cc'
            placeholder={t('email.cc')}
            value={formValues.cc}
            onChange={handleInputChange}
            error={Boolean(props.formik.errors?.cc)}
            helperText={Boolean(props.formik.errors?.cc)}
          />
        </Box>

        <Box className='w-full'>
          <InputLabel>{t('email.bcc')}</InputLabel>
          <TextField
            name='bcc'
            placeholder={t('email.bcc')}
            value={formValues.bcc}
            onChange={handleInputChange}
            error={Boolean(props.formik.errors?.bcc)}
            helperText={props.formik.errors?.bcc}
          />
        </Box>
      </BaseRow>
      <BaseRow className='gap-[20px] mb-[20px] mt-[20px]'>
        <Box className='w-full'>
          <InputLabel>{t('email.subject')}</InputLabel>
          <TextField
            name='subject'
            placeholder={t('email.subject')}
            value={formValues.subject}
            onChange={handleInputChange}
            error={Boolean(props.formik.errors?.subject)}
            helperText={props.formik.errors?.subject}
          />
        </Box>
      </BaseRow>
      <BaseRow className='gap-[20px] mb-[20px] mt-[20px]'>
        <Box className='w-full'>
          <InputLabel>{t('email.template')}</InputLabel>
          <BaseSelect
            options={templateList}
            name='template'
            isHideIconCheckbox={true}
            value={formValues.template}
            onChange={(e) => handleSelectChange(e.target.value)}
            error={Boolean(props.formik.errors?.template)}
            helperText={props.formik.errors?.template}
          />
        </Box>
      </BaseRow>
      <BaseRow className='gap-[20px] mb-[20px] mt-[20px]'>
        <Box className='w-full'>
          <BaseRichEditor
            onCreateNew={(value) => {
              const updatedValues = { ...formValues, note: value };
              setFormValues(updatedValues);
              onChange(updatedValues);
            }}
            valueProps={formValues.note}
            isError={Boolean(props.formik.errors?.note)}
            errorMessage={props.formik.errors?.note}
          />
        </Box>
      </BaseRow>

      {props.jobId ? (
        <FileItem jobId={props.jobId ?? ''} form={props.formik} typeData={FILE_ITEM_FOR_TASK} />
      ) : (
        <FileItem customerId={props.customerId ?? ''} form={props.formik} typeData={FILE_ITEM_FOR_CUSTOMER} />
      )}
    </Box>
  );
};

export default BaseSendEmail;
